import type { ColorSpaceId } from '../../color/matrix';

/**
 * A decoded, upright (orientation already applied) source image.
 *  · `u8`: 8-bit RGBA, already converted by the browser into Display P3 (sRGB transfer curve).
 *  · `u16`: 16-bit RGBA in its own tagged space (`space`, transfer per space; sRGB if untagged).
 *           Converted to linear P3 with our matrices at upload time (§6.1 decode step 4).
 */
export type DecodedImage =
  | {
      kind: 'u8';
      width: number;
      height: number;
      data: Uint8ClampedArray;
      space: 'p3';
      hasAlpha: boolean;
      source: SourceInfo;
    }
  | {
      kind: 'u16';
      width: number;
      height: number;
      data: Uint16Array;
      space: ColorSpaceId;
      hasAlpha: boolean;
      source: SourceInfo;
    };

export type SourceInfo = {
  format: 'jpeg' | 'png' | 'tiff' | 'heic';
  bitDepth: 8 | 16;
  orientation: number; // EXIF orientation as found (1..8); pixels are already upright
  embeddedSpace: ColorSpaceId | 'other' | 'none';
  /** Width/height as stored in the file, before orientation. */
  storedWidth: number;
  storedHeight: number;
};

export type ExifSummary = {
  orientation: number;
  date?: string; // YYYY-MM-DD
  artist?: string;
  copyright?: string;
  hasGps: boolean;
};

export class DecodeError extends Error {
  constructor(
    message: string,
    readonly code: 'unsupported' | 'corrupt' | 'heic-unavailable',
  ) {
    super(message);
    // The name crosses the worker boundary (Comlink keeps name + message), so the UI can translate it.
    this.name = `DecodeError:${code}`;
  }
}

export const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic', '.heif', '.tif', '.tiff'];
export function isAcceptedName(name: string): boolean {
  const lower = name.toLowerCase();
  // Our own files next to the photos (the background matte cache is a PNG) are never photos.
  if (lower.endsWith('.epb.mask.png') || lower.endsWith('.epb.json')) return false;
  return ACCEPTED_EXTENSIONS.some((e) => lower.endsWith(e));
}

/** Sniffs the format from magic bytes (extension is only a hint). */
export function sniffFormat(head: Uint8Array): SourceInfo['format'] | null {
  if (head[0] === 0xff && head[1] === 0xd8) return 'jpeg';
  if (head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47) return 'png';
  if (
    (head[0] === 0x49 && head[1] === 0x49 && head[2] === 42 && head[3] === 0) ||
    (head[0] === 0x4d && head[1] === 0x4d && head[2] === 0 && head[3] === 42)
  )
    return 'tiff';
  const box = String.fromCharCode(...head.subarray(4, 12));
  if (box.startsWith('ftyp') && /heic|heix|hevc|heim|heis|mif1|msf1/.test(box.slice(4))) return 'heic';
  return null;
}
